/**
 * Centralized Database Operations for Drill Library
 * 
 * This module handles all database interactions for the drill library.
 * IMPORTANT: Drills and categories are SHARED resources (global library),
 * not user-scoped. All users see the same drills. Only admins can modify them.
 * 
 * We still use ScopedDatabase for consistency but bypass the access control
 * injection since drills don't need allowRead/allowWrite fields.
 */

import { 
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  writeBatch,
  query,
  where,
  orderBy,
  Unsubscribe,
  DocumentData,
  QuerySnapshot,
  DocumentReference
} from 'firebase/firestore';
import logger from '@/lib/logging';
import { ScopedDatabase } from '@/components/authnz/lib/scoped-db';
import { getDb } from '@/lib/firebase-config';
import { Drill, Category } from '../types/drill-types';
import { getVideoIdFromDrill } from './video-matcher.js';

// Collection paths
export const DRILLS_COLLECTION = 'drills';
export const CATEGORIES_COLLECTION = 'categories';

/**
 * Drill database operations class
 * 
 * IMPORTANT: Drills are a SHARED global resource.
 * - All users can READ all drills (shared library)
 * - Only admins can CREATE/UPDATE/DELETE drills
 * - No user-scoping (allowRead/allowWrite) is applied to drills
 */
export class DrillDatabase {
  constructor(
    private scopedDb: ScopedDatabase, 
    private userId?: string,
    private isAdmin: boolean = false
  ) {}

  /**
   * Get current user ID for audit fields
   */
  private getCurrentUserId(): string {
    return this.userId || 'system';
  }

  /**
   * Check if user is admin and throw error if not
   * All write operations on drills require admin privileges
   */
  private requireAdmin(operation: string): void {
    if (!this.isAdmin) {
      const error = `🚫 PERMISSION DENIED: ${operation} requires Administrator privileges. Current user: ${this.userId || 'unknown'}`;
      logger.error(error);
      throw new Error(error);
    }
  }

  /**
   * Add audit fields for creation
   */
  private addCreationAudit(data: any): any {
    return {
      ...data,
      createdBy: this.getCurrentUserId(),
      createdAt: new Date().toISOString()
    };
  }

  /**
   * Add audit fields for updates
   */
  private addUpdateAudit(data: any): any {
    return {
      ...data,
      changedBy: this.getCurrentUserId(),
      changedAt: new Date().toISOString()
    };
  }

  // ============ DRILL OPERATIONS ============
  // Note: Drills are SHARED - we use raw Firestore to avoid adding allowRead/allowWrite

  /**
   * Create a new drill (SHARED resource - ADMIN ONLY)
   */
  async createDrill(drillData: Omit<Drill, 'id'>): Promise<DocumentReference> {
    this.requireAdmin('Create drill');
    
    try {
      // Filter out undefined values
      const cleanData = Object.fromEntries(
        Object.entries(drillData).filter(([_, value]) => value !== undefined)
      );

      // Add creation audit fields
      const drillWithAudit = this.addCreationAudit(cleanData);

      // Use raw Firestore - drills are SHARED, no allowRead/allowWrite needed
      const db = getDb();
      const drillsCollection = collection(db, DRILLS_COLLECTION);
      return await addDoc(drillsCollection, drillWithAudit);
    } catch (error) {
      logger.error(`❌ Failed to create drill "${drillData.name}":`, error);
      logger.error('   Drill data keys:', Object.keys(drillData));
      logger.error(`   Tags type: ${typeof drillData.tags} (${Array.isArray(drillData.tags) ? 'is array' : 'is object'})`);
      throw error;
    }
  }

  /**
   * Update an existing drill (SHARED resource - ADMIN ONLY)
   */
  async updateDrill(drillId: string, updatedData: Partial<Drill>): Promise<void> {
    this.requireAdmin('Update drill');
    
    // Add update audit fields
    const dataWithAudit = this.addUpdateAudit(updatedData);

    // Use raw Firestore - drills are SHARED
    const db = getDb();
    const drillDoc = doc(db, DRILLS_COLLECTION, drillId);
    return await updateDoc(drillDoc, dataWithAudit);
  }

  /**
   * Delete a drill (SHARED resource - ADMIN ONLY)
   */
  async deleteDrill(drillId: string): Promise<void> {
    this.requireAdmin('Delete drill');
    
    const db = getDb();
    const drillDoc = doc(db, DRILLS_COLLECTION, drillId);
    return await deleteDoc(drillDoc);
  }

  /**
   * Delete multiple drills in batch (SHARED resource - ADMIN ONLY)
   * Uses chunked batches to handle Firestore's 500 operation limit per batch
   */
  async deleteDrillsBatch(drillIds: string[]): Promise<void> {
    this.requireAdmin('Batch delete drills');
    
    const db = getDb();
    const BATCH_SIZE = 500; // Firestore batch operation limit
    
    // Process deletes in chunks of 500
    for (let i = 0; i < drillIds.length; i += BATCH_SIZE) {
      const chunk = drillIds.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      
      chunk.forEach(id => {
        const drillDoc = doc(db, DRILLS_COLLECTION, id);
        batch.delete(drillDoc);
      });
      
      await batch.commit();
    }
  }

  /**
   * Delete ALL drills in the shared library (ADMIN ONLY)
   * Uses chunked batches to handle Firestore's 500 operation limit per batch
   */
  async deleteAllDrills(): Promise<void> {
    this.requireAdmin('Delete all drills');
    
    logger.debug('🔴🔴🔴 deleteAllDrills() STARTING - Getting Firestore instance...');
    const db = getDb();
    logger.debug('🔴 Firestore instance obtained, getting drills collection...');
    
    const drillsCollection = collection(db, DRILLS_COLLECTION);
    logger.debug(`🔴 Collection reference created for: ${DRILLS_COLLECTION}`);
    
    logger.debug('🔴 Fetching ALL documents from drills collection...');
    const snapshot = await getDocs(drillsCollection);

    const docs = snapshot.docs;
    const BATCH_SIZE = 500; // Firestore batch operation limit
    
    logger.debug(`🗑️🗑️🗑️ Found ${docs.length} drills to delete from shared library`);
    
    if (docs.length === 0) {
      logger.debug('⚠️ No drills found to delete!');
      return;
    }
    
    // Process deletes in chunks of 500
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const chunk = docs.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      
      chunk.forEach(d => {
        logger.debug(`  - Marking for deletion: ${d.id}`);
        batch.delete(d.ref);
      });
      
      logger.debug(`🔴 Committing batch delete for ${chunk.length} drills...`);
      await batch.commit();
      logger.debug(`🗑️ Deleted batch ${Math.floor(i / BATCH_SIZE) + 1} (${chunk.length} drills)`);
    }
    
    logger.debug(`✅✅✅ Successfully deleted all ${docs.length} drills from shared library`);
  }

  /**
   * Get all drills from the shared library
   */
  async getAllDrills(): Promise<Drill[]> {
    const db = getDb();
    const drillsCollection = collection(db, DRILLS_COLLECTION);
    const snapshot = await getDocs(drillsCollection);
    
    return snapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    } as Drill));
  }

  /**
   * Listen to drills collection with real-time updates (SHARED resource)
   */
  listenToDrills(callback: (drills: Drill[]) => void): Unsubscribe {
    const db = getDb();
    const drillsCollection = collection(db, DRILLS_COLLECTION);
    
    return onSnapshot(drillsCollection, (snapshot) => {
      const drills = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as Drill));
      callback(drills);
    }, (error) => {
      logger.error('Error listening to drills:', error);
    });
  }

  // ============ CATEGORY OPERATIONS ============
  // Note: Categories are also SHARED resources

  /**
   * Create a new category (SHARED resource - ADMIN ONLY)
   */
  async createCategory(categoryData: Omit<Category, 'id'>): Promise<DocumentReference> {
    this.requireAdmin('Create category');
    
    const categoryWithAudit = this.addCreationAudit(categoryData);
    const db = getDb();
    const categoriesCollection = collection(db, CATEGORIES_COLLECTION);
    return await addDoc(categoriesCollection, categoryWithAudit);
  }

  /**
   * Update a category (SHARED resource - ADMIN ONLY)
   */
  async updateCategory(categoryId: string, updatedData: Partial<Category>): Promise<void> {
    this.requireAdmin('Update category');
    
    const dataWithAudit = this.addUpdateAudit(updatedData);
    const db = getDb();
    const categoryDoc = doc(db, CATEGORIES_COLLECTION, categoryId);
    return await updateDoc(categoryDoc, dataWithAudit);
  }

  /**
   * Delete a category (SHARED resource - ADMIN ONLY)
   */
  async deleteCategory(categoryId: string): Promise<void> {
    this.requireAdmin('Delete category');
    
    const db = getDb();
    const categoryDoc = doc(db, CATEGORIES_COLLECTION, categoryId);
    return await deleteDoc(categoryDoc);
  }

  /**
   * Listen to categories collection (SHARED resource)
   */
  listenToCategories(callback: (categories: Category[]) => void): Unsubscribe {
    const db = getDb();
    const categoriesCollection = collection(db, CATEGORIES_COLLECTION);
    
    return onSnapshot(categoriesCollection, (snapshot) => {
      const categories = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as Category));
      callback(categories);
    }, (error) => {
      logger.error('Error listening to categories:', error);
    });
  }

  // ============ BULK OPERATIONS ============

  /**
   * Bulk update drills with audit fields (SHARED resource - ADMIN ONLY)
   * Uses chunked batches to handle Firestore's 500 operation limit per batch
   */
  async bulkUpdateDrills(updates: { id: string; data: Partial<Drill> }[]): Promise<void> {
    this.requireAdmin('Bulk update drills');
    
    const db = getDb();
    const BATCH_SIZE = 500; // Firestore batch operation limit
    
    // Process updates in chunks of 500
    for (let i = 0; i < updates.length; i += BATCH_SIZE) {
      const chunk = updates.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      
      chunk.forEach(({ id, data }) => {
        const drillDoc = doc(db, DRILLS_COLLECTION, id);
        const dataWithAudit = this.addUpdateAudit(data);
        batch.update(drillDoc, dataWithAudit);
      });
      
      await batch.commit();
    }
  }

  /**
   * Import drills with proper audit fields
   * Overwrites existing data based on video URL matching
   */
  async importDrills(drills: Omit<Drill, 'id'>[], overwriteExisting: boolean = true): Promise<{
    imported: number;
    updated: number;
    errors: string[];
  }> {
    const errors: string[] = [];
    let imported = 0;
    let updated = 0;

    try {
      // Get existing drills if we need to check for duplicates
      const existingDrills = overwriteExisting ? await this.getAllDrills() : [];
      // Use centralized video matcher to index by video ID
      const existingByVideoId = new Map<string, Drill>();
      
      existingDrills.forEach(drill => {
        const videoId = getVideoIdFromDrill(drill);
        if (videoId) {
          existingByVideoId.set(videoId, drill);
        }
      });

      // Process each drill
      for (const drill of drills) {
        try {
          const drillVideoId = getVideoIdFromDrill(drill);
          const existing = drillVideoId ? existingByVideoId.get(drillVideoId) : undefined;
          
          if (existing && overwriteExisting) {
            // Update existing drill, preserving original creation info
            const updateData = {
              ...drill,
              createdBy: existing.createdBy || drill.createdBy,
              createdAt: existing.createdAt || drill.createdAt
            };
            await this.updateDrill(existing.id, updateData);
            updated++;
          } else if (!existing) {
            // Create new drill
            await this.createDrill(drill);
            imported++;
          }
        } catch (error) {
          errors.push(`Failed to import drill "${drill.name}": ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }
    } catch (error) {
      errors.push(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return { imported, updated, errors };
  }

  /**
   * Export drills with all audit fields
   */
  async exportDrills(): Promise<Drill[]> {
    return await this.getAllDrills();
  }

  // ============ TAG OPERATIONS ============

  /**
   * Update tags for a drill
   */
  async updateDrillTags(drillId: string, tags: { [key: string]: string | string[] }): Promise<void> {
    return await this.updateDrill(drillId, { tags });
  }

  /**
   * Bulk update tags for multiple drills
   */
  async bulkUpdateTags(drillIds: string[], tags: { [key: string]: string | string[] }): Promise<void> {
    const updates = drillIds.map(id => ({
      id,
      data: { tags }
    }));
    return await this.bulkUpdateDrills(updates);
  }

  /**
   * Add tag to multiple drills
   */
  async addTagToDrills(drillIds: string[], tagKey: string, tagValue: string | string[]): Promise<void> {
    const drills = await this.getAllDrills();
    const drillsMap = new Map(drills.map(d => [d.id, d]));
    
    const updates = drillIds.map(id => {
      const drill = drillsMap.get(id);
      if (!drill) return null;
      
      const currentTags = drill.tags || {};
      const updatedTags = {
        ...currentTags,
        [tagKey]: tagValue
      };
      
      return {
        id,
        data: { tags: updatedTags }
      };
    }).filter(Boolean) as { id: string; data: Partial<Drill> }[];

    if (updates.length > 0) {
      return await this.bulkUpdateDrills(updates);
    }
  }

  /**
   * Remove tag from multiple drills
   */
  async removeTagFromDrills(drillIds: string[], tagKey: string): Promise<void> {
    const drills = await this.getAllDrills();
    const drillsMap = new Map(drills.map(d => [d.id, d]));
    
    const updates = drillIds.map(id => {
      const drill = drillsMap.get(id);
      if (!drill) return null;
      
      const currentTags = { ...(drill.tags || {}) };
      delete currentTags[tagKey];
      
      return {
        id,
        data: { tags: currentTags }
      };
    }).filter(Boolean) as { id: string; data: Partial<Drill> }[];

    if (updates.length > 0) {
      return await this.bulkUpdateDrills(updates);
    }
  }
}

/**
 * Factory function to create a DrillDatabase instance
 * @param scopedDb - The scoped database instance
 * @param userId - The current user's ID (for audit fields)
 * @param isAdmin - Whether the current user is an administrator (required for write operations)
 */
export function createDrillDatabase(scopedDb: ScopedDatabase, userId?: string, isAdmin: boolean = false): DrillDatabase {
  return new DrillDatabase(scopedDb, userId, isAdmin);
}
